import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import API from '../../services/API'
import { DemoPersonaList } from './DemoPersonaPicker'

// Blocking first-visit modal for the demo tenant: step 1 is the shared-demo
// disclaimer (acknowledged once per demo visitor), step 2 is the persona
// picker. Mirrors the LegalAgreementModal blocking-dialog precedent.
const DemoWelcomeModal = ({
  open,
  onClose,
  personas,
  currentPersonaKey,
  anonymous = false,
  onAcknowledged,
}) => {
  const [step, setStep] = useState('disclaimer')
  const [understood, setUnderstood] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  const handleContinue = async () => {
    setError(false)
    setSaving(true)

    // Anonymous sessions: the server no-ops this (200) — acknowledgement is
    // remembered client-side via onAcknowledged (localStorage). Credentialed
    // sessions keep the server-recorded acknowledgement.
    const res = await API.post(
      '/demo/acknowledge-disclaimer',
      {},
      { skipGlobalErrorHandler: true },
    )

    setSaving(false)

    const acknowledged = anonymous
      ? res.status === 200
      : res.status === 200 && res.data?.acknowledged

    if (acknowledged) {
      onAcknowledged?.()
      setStep('personas')
      return
    }

    setError(true)
  }

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={() => {}}
    >
      {step === 'disclaimer' ? (
        <>
          <DialogTitle>Welcome to the AmbuNet demo</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography>
                Before you dive in, a few things to know about this
                environment:
              </Typography>

              <Alert severity="warning">
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  This is a shared demo environment.
                </Typography>
                <Typography variant="body2">
                  Other people may be exploring it right now, using the same
                  demo personas as you.
                </Typography>
              </Alert>

              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Personas are shared.</strong> Anything you enter
                  while acting as a persona (for example "the manager") can be
                  seen, changed, or deleted by other demo users acting as the
                  same people — so please don't enter anything real, personal,
                  or confidential.
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Content is not moderated.</strong> AmbuNet takes no
                  responsibility for content entered by other demo users.
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  <strong>Everything resets nightly.</strong> All demo data is
                  wiped and restored to a fresh state every night, so nothing
                  you enter will be kept.
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Best explored on a computer.</strong> The management,
                  governance, control-room and PTS screens are designed for
                  desktop; the mobile-sized view is designed for frontline
                  staff on shift.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" onClose={() => setError(false)}>
                  Something went wrong. Please try again.
                </Alert>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={understood}
                    onChange={(e) => setUnderstood(e.target.checked)}
                  />
                }
                label="I understand"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              disabled={!understood || saving}
              onClick={handleContinue}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Continue'
              )}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Who would you like to explore as?</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each persona sees a different side of AmbuNet. You can switch at
              any time from the menu in the top-right corner.
            </Typography>
            <DemoPersonaList
              personas={personas}
              currentPersonaKey={currentPersonaKey}
              onCurrentSelected={onClose}
            />
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

export default DemoWelcomeModal
